import { createSelector } from 'reselect'
import { merge } from 'lodash'

import { actionTypeCreator } from './util'
import { getAliadas } from './entities'

// /////////////////////////////////////////////////////////////////////////////
// Constants
// /////////////////////////////////////////////////////////////////////////////
export const constants = {
  NAME: 'service'
}

// /////////////////////////////////////////////////////////////////////////////
// Action Types
// /////////////////////////////////////////////////////////////////////////////
const createActionType = actionTypeCreator.bind(null, constants.NAME)
export const UPDATE_NEW_SERVICE = createActionType('UPDATE_NEW_SERVICE')

export const REDEEM_PROMO_CODE = createActionType('REDEEM_PROMO_CODE')
export const REDEEM_PROMO_CODE_STARTED = createActionType('REDEEM_PROMO_CODE_STARTED')
export const REDEEM_PROMO_CODE_SUCCESS = createActionType('REDEEM_PROMO_CODE_SUCCESS')
export const REDEEM_PROMO_CODE_FAILURE = createActionType('REDEEM_PROMO_CODE_FAILURE')

export const CREATE = createActionType('CREATE')
export const CREATE_STARTED = createActionType('CREATE_STARTED')
export const CREATE_SUCCESS = createActionType('CREATE_SUCCESS')
export const CREATE_FAILURE = createActionType('CREATE_FAILURE')

export const FETCH = createActionType('FETCH')
export const FETCH_STARTED = createActionType('FETCH_STARTED')
export const FETCH_SUCCESS = createActionType('FETCH_SUCCESS')
export const FETCH_FAILURE = createActionType('FETCH_FAILURE')

export const UPDATE = createActionType('UPDATE')
export const UPDATE_STARTED = createActionType('UPDATE_STARTED')
export const UPDATE_SUCCESS = createActionType('UPDATE_SUCCESS')
export const UPDATE_FAILURE = createActionType('UPDATE_FAILURE')

export const CANCEL = createActionType('CANCEL')
export const CANCEL_STARTED = createActionType('CANCEL_STARTED')
export const CANCEL_SUCCESS = createActionType('CANCEL_SUCCESS')
export const CANCEL_FAILURE = createActionType('CANCEL_FAILURE')

export const CLEAR_PENALTY = createActionType('CLEAR_PENALTY')

export const FETCH_PENALTY = createActionType('FETCH_PENALTY')
export const FETCH_PENALTY_STARTED = createActionType('FETCH_PENALTY_STARTED')
export const FETCH_PENALTY_SUCCESS = createActionType('FETCH_PENALTY_SUCCESS')
export const FETCH_PENALTY_FAILURE = createActionType('FETCH_PENALTY_FAILURE')

// /////////////////////////////////////////////////////////////////////////////
// Actions
// /////////////////////////////////////////////////////////////////////////////
export function updateNewService (data) {
  return {
    type: UPDATE_NEW_SERVICE,
    payload: data
  }
}

export function redeemPromoCode (code) {
  return {
    type: REDEEM_PROMO_CODE,
    payload: code
  }
}

export function create () {
  return {
    type: CREATE
  }
}

export function fetch (serviceId) {
  return {
    type: FETCH,
    payload: { serviceId }
  }
}

export function update (serviceId, serviceChanges, next) {
  return {
    type: UPDATE,
    payload: { serviceId, serviceChanges },
    meta: { next }
  }
}

export function cancel (serviceId, next) {
  return {
    type: CANCEL,
    payload: { serviceId },
    meta: { next }
  }
}

export function clearPenalty () {
  return {
    type: CLEAR_PENALTY
  }
}

export function fetchUpdatePenalty (serviceId, serviceChanges) {
  return fetchPenalty({ serviceId, serviceChanges, method: 'PUT' })
}

export function fetchCancelPenalty (serviceId) {
  return fetchPenalty({ serviceId, method: 'DELETE' })
}

function fetchPenalty ({ serviceId, serviceChanges, method }) {
  return {
    type: FETCH_PENALTY,
    payload: {
      serviceId,
      serviceChanges,
      method
    }
  }
}

// /////////////////////////////////////////////////////////////////////////////
// Selectors
// /////////////////////////////////////////////////////////////////////////////
export const getState = (state) => state[constants.NAME]

export const getService = createSelector(
  getState,
  (state) => state.service
)

export const getPromoCode = createSelector(
  getState,
  (state) => state.promoCode
)

export const getSelectedAliada = createSelector(
  getAliadas,
  getService,
  (aliadas, service) => aliadas[service.aliadaId]
)

export const getPenalty = createSelector(
  getState,
  (state) => state.penalty
)

// /////////////////////////////////////////////////////////////////////////////
// Queries
// /////////////////////////////////////////////////////////////////////////////
export const isCreating = createSelector(
  getState,
  (state) => state.creating
)

export const isFetching = createSelector(
  getState,
  (state) => state.fetching
)

export const isUpdating = createSelector(
  getState,
  (state) => state.updating
)

export const isCancelling = createSelector(
  getState,
  (state) => state.cancelling
)

export const isRedeemingPromoCode = createSelector(
  getState,
  (state) => state.redeemingPromoCode
)

// /////////////////////////////////////////////////////////////////////////////
// Reducer
// /////////////////////////////////////////////////////////////////////////////
const initialState = {
  service: {
    serviceTypeId: '1',
    estimatedHours: '4',
    aliadaId: undefined,
    date: undefined,
    time: undefined,
    instructions: '',
    promotionalCode: '',
    address: {
      street: '',
      number: '',
      interiorNumber: '',
      neighborhood: '',
      city: '',
      state: '',
      lat: '',
      lng: ''
    },
    keysLocation: 'home',
    weekdays: []
  },
  user: {},
  promoCode: {},
  paymentTokens: {},
  penalty: {},
  creating: false,
  fetching: false,
  updating: false,
  cancelling: false,
  redeemingPromoCode: false,
  error: null
}

export function reducer (state = initialState, action = {}) {
  const { type, payload } = action
  switch (type) {
    case UPDATE_NEW_SERVICE:
      return merge({}, state, action.payload)

    case REDEEM_PROMO_CODE:
      return Object.assign({}, state, {
        promoCode: {}
      })
    case REDEEM_PROMO_CODE_STARTED:
      return Object.assign({}, state, {
        redeemingPromoCode: true,
        promoCode: {}
      })
    case REDEEM_PROMO_CODE_SUCCESS:
      return Object.assign({}, state, {
        redeemingPromoCode: false,
        promoCode: {
          valid: true,
          error: null,
          ...action.payload
        }
      })
    case REDEEM_PROMO_CODE_FAILURE:
      return Object.assign({}, state, {
        redeemingPromoCode: false,
        promoCode: {
          valid: false,
          error: action.payload
        }
      })

    case CREATE_STARTED:
      return {
        ...state,
        creating: true
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        promoCode: {}
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        error: payload
      }

    case FETCH_STARTED:
      return {
        ...state,
        fetching: true
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        fetching: false
      }
    case FETCH_FAILURE:
      return {
        ...state,
        fetching: false,
        error: payload
      }

    case UPDATE_STARTED:
      return {
        ...state,
        updating: true
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        error: payload
      }

    case CANCEL_STARTED:
      return {
        ...state,
        cancelling: true
      }
    case CANCEL_SUCCESS:
      return {
        ...state,
        cancelling: false
      }
    case CANCEL_FAILURE:
      return {
        ...state,
        cancelling: false,
        error: payload
      }

    case CLEAR_PENALTY:
      return {
        ...state,
        penalty: {}
      }

    case FETCH_PENALTY_STARTED:
      return state
    case FETCH_PENALTY_SUCCESS:
      return {
        ...state,
        penalty: payload
      }
    case FETCH_PENALTY_FAILURE:
      return {
        ...state,
        penalty: {},
        error: payload
      }

    default:
      return state
  }
}



// WEBPACK FOOTER //
// ./src/redux/modules/service.js